import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useAuth } from '../../contexts/AuthContext';
import {
  emailApi,
  EmailAccount,
  EmailFolder,
  EmailMessageRow,
  EmailMessageFull,
} from '../../services/emailApi';
import Composer from './Composer';
import '../Dashboard/Dashboard.css';
import './MailPage.css';

const POLL_INTERVAL_MS = 30000;

type Status = 'idle' | 'loading' | 'syncing' | 'error';

const MailPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<number | null>(null);
  const [folders, setFolders] = useState<EmailFolder[]>([]);
  const [activeFolder, setActiveFolder] = useState<string>('INBOX');
  const [messages, setMessages] = useState<EmailMessageRow[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<EmailMessageFull | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState<
    null | { mode: 'compose' | 'reply' | 'forward'; replyTo?: any; forwardFrom?: any }
  >(null);

  // Mobile-навигация: какая панель видима на узких экранах
  const [mobileView, setMobileView] = useState<'sidebar' | 'list' | 'reader'>('list');

  // ── Загрузка аккаунтов ─────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setStatus('loading');
        const accs = await emailApi.listAccounts();
        if (!alive) return;
        setAccounts(accs);
        if (accs.length > 0) {
          const def = accs.find((a) => a.is_default) ?? accs[0];
          setActiveAccountId(def.id);
        }
        setStatus('idle');
      } catch (err: any) {
        if (!alive) return;
        setStatus('error');
        setErrorMsg(err?.response?.data?.detail || String(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ── Папки текущего аккаунта ────────────────────────────────
  useEffect(() => {
    if (!activeAccountId) return;
    let alive = true;
    (async () => {
      try {
        const fs = await emailApi.listFolders(activeAccountId);
        if (!alive) return;
        setFolders(fs);
        const inbox = fs.find((f) => f.special === 'inbox');
        setActiveFolder(inbox?.raw ?? 'INBOX');
      } catch (err: any) {
        console.warn('[MAIL] folders error', err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [activeAccountId]);

  // ── Список писем ───────────────────────────────────────────
  const loadMessages = useCallback(async () => {
    if (!activeAccountId) return;
    try {
      const res = await emailApi.listMessages(activeAccountId, {
        folder: activeFolder,
        page: 1,
        page_size: 50,
        search: searchQuery || undefined,
        unread_only: unreadOnly,
      });
      setMessages(res.items);
    } catch (err) {
      console.warn('[MAIL] messages error', err);
    }
  }, [activeAccountId, activeFolder, searchQuery, unreadOnly]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Polling каждые 30 сек
  useEffect(() => {
    const t = setInterval(loadMessages, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [loadMessages]);

  // ── Sync — pull новые письма с IMAP ────────────────────────
  const handleSync = async () => {
    if (!activeAccountId) return;
    setStatus('syncing');
    try {
      await emailApi.syncFolder(activeAccountId, activeFolder, 50);
      await loadMessages();
      setStatus('idle');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || 'Ошибка синхронизации');
    }
  };

  // ── Загрузить старее (пагинация в прошлое) ───────────────
  const handleLoadOlder = async () => {
    if (!activeAccountId || messages.length === 0) return;
    setStatus('syncing');
    try {
      const minUid = messages.reduce((m, x) => (x.imap_uid < m ? x.imap_uid : m), messages[0].imap_uid);
      const res = await emailApi.syncFolder(activeAccountId, activeFolder, 50, minUid);
      await loadMessages();
      setStatus('idle');
      if (res.added === 0) {
        alert(`Старее писем нет. Всего в папке на сервере: ${res.total_in_folder ?? '?'}`);
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || 'Ошибка');
    }
  };

  // ── Открыть письмо ─────────────────────────────────────────
  const handleSelectMessage = async (row: EmailMessageRow) => {
    try {
      const full = await emailApi.getMessage(row.id);
      setSelectedMessage(full);
      setMobileView('reader');
      // Авто-mark as read
      if (!row.is_seen) {
        await emailApi.setFlag(row.id, { seen: true });
        setMessages((cur) => cur.map((m) => (m.id === row.id ? { ...m, is_seen: true } : m)));
      }
    } catch (err) {
      console.warn('[MAIL] message open error', err);
    }
  };

  const handleFlag = async (msgId: number, flagged: boolean) => {
    await emailApi.setFlag(msgId, { flagged });
    setMessages((cur) => cur.map((m) => (m.id === msgId ? { ...m, is_flagged: flagged } : m)));
    if (selectedMessage?.id === msgId) {
      setSelectedMessage({ ...selectedMessage, is_flagged: flagged });
    }
  };

  const handleDelete = async (msgId: number) => {
    if (!window.confirm('Переместить письмо в Корзину?')) return;
    await emailApi.deleteMessage(msgId);
    setMessages((cur) => cur.filter((m) => m.id !== msgId));
    if (selectedMessage?.id === msgId) setSelectedMessage(null);
  };

  // ── Empty states ───────────────────────────────────────────
  if (accounts.length === 0 && status === 'idle') {
    return (
      <div className="mail-empty">
        <h2>📧 Почта</h2>
        <p>У вас пока нет настроенных почтовых аккаунтов.</p>
        <button className="btn-primary" onClick={() => navigate('/settings/email')}>
          ⚙️ Настроить почту
        </button>
        <p className="mail-empty-hint">
          В настройках укажите email и пароль приложения — IMAP/SMTP сервера определятся автоматически
          для yandex.ru, gmail.com, mail.ru и других.
        </p>
      </div>
    );
  }

  const activeAccount = accounts.find((a) => a.id === activeAccountId);

  return (
    <div className={`mail-layout mobile-view-${mobileView}`}>
      {/* Sidebar в стиле основного дашборда */}
      <aside className="mail-sidebar dashboard-sidebar">
        {/* Профиль */}
        <div className="sidebar-profile">
          <div className="profile-container">
            <div className="profile-avatar">{user?.login?.[0]?.toUpperCase() || 'U'}</div>
            <div className="profile-info">
              <div className="profile-name">{user?.login}</div>
              <div className="profile-role">{user?.is_admin ? 'Администратор' : 'Оператор'}</div>
            </div>
          </div>
        </div>

        {/* Написать */}
        <div className="mail-compose-wrap">
          <button
            className="mail-compose-btn"
            onClick={() => setComposerOpen({ mode: 'compose' })}
            disabled={accounts.length === 0}
          >
            <i className="bi bi-pencil-square"></i>
            <span>Написать</span>
          </button>
        </div>

        {/* Папки */}
        <div className="sidebar-groups">
          <div className="groups-header">
            <div className="groups-title">ПАПКИ</div>
          </div>
          <div className="groups-list">
            {folders.length === 0 && (
              <div style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.875rem' }}>
                Загрузка...
              </div>
            )}
            {folders.map((f) => (
              <div
                key={f.raw}
                className={`group-item ${f.raw === activeFolder ? 'active' : ''}`}
                onClick={() => { setActiveFolder(f.raw); setMobileView('list'); }}
                title={f.raw}
              >
                <i className={`bi bi-${folderIconBi(f.special)}`}></i>
                <span>{folderLabel(f)}</span>
                {typeof f.unseen === 'number' && f.unseen > 0 &&
                  !['sent', 'drafts', 'trash', 'junk'].includes(f.special || '') && (
                  <span
                    className="folder-unread-badge"
                    title={`Непрочитанных: ${f.unseen}${typeof f.total === 'number' ? ` из ${f.total}` : ''}`}
                  >
                    {f.unseen}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* В чаты + аккаунты + управление */}
        <div className="sidebar-admin">
          <div className="admin-item" onClick={() => navigate('/dashboard')}>
            <i className="bi bi-chat-dots"></i>
            <span>В чаты</span>
          </div>

          <div className="admin-header" style={{ marginTop: '0.5rem' }}>
            <div className="admin-title">АККАУНТЫ</div>
          </div>
          {accounts.map((a) => (
            <div
              key={a.id}
              className={`admin-item ${a.id === activeAccountId ? 'active' : ''}`}
              onClick={() => setActiveAccountId(a.id)}
              title={a.email}
            >
              <i className={`bi ${a.oauth_provider ? 'bi-shield-lock' : 'bi-envelope'}`}></i>
              <span className="mail-account-name">{a.label || a.email}</span>
              {a.last_sync_status === 'error' && (
                <i className="bi bi-exclamation-triangle-fill" style={{ color: '#cc0033', marginLeft: 'auto' }}></i>
              )}
            </div>
          ))}
          <div className="admin-item" onClick={() => navigate('/settings/email')}>
            <i className="bi bi-gear"></i>
            <span>Управление аккаунтами</span>
          </div>
        </div>

        {/* Выход */}
        <div className="sidebar-logout">
          <button onClick={logout} className="logout-btn">
            <i className="bi bi-box-arrow-right"></i>
            Выход
          </button>
        </div>
      </aside>

      {/* Message list */}
      <section className="mail-list">
        <div className="mail-list-toolbar">
          <button
            className="mobile-only mail-icon-btn"
            onClick={() => setMobileView('sidebar')}
            title="Меню"
          >
            <i className="bi bi-list"></i>
          </button>
          <input
            type="search"
            placeholder="🔍 Поиск по теме, отправителю, тексту…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mail-search"
          />
          <label className="mail-toolbar-check">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            Только непрочитанные
          </label>
          <button onClick={handleSync} disabled={status === 'syncing'} className="btn-secondary">
            {status === 'syncing' ? '⏳ Синхрон…' : '🔄 Обновить'}
          </button>
        </div>

        {activeAccount?.last_sync_status === 'error' && (
          <div className="mail-error-banner">
            ⚠️ Ошибка sync: {activeAccount.last_sync_error}
          </div>
        )}

        <div className="mail-list-items">
          {messages.length === 0 ? (
            <div className="mail-hint">📭 Писем нет. Нажмите «🔄 Обновить» для загрузки с IMAP.</div>
          ) : (
            <>
            {messages.map((m) => (
              <div
                key={m.id}
                className={`mail-row ${selectedMessage?.id === m.id ? 'selected' : ''} ${
                  m.is_seen ? '' : 'unread'
                }`}
                onClick={() => handleSelectMessage(m)}
              >
                <span
                  className={`mail-star ${m.is_flagged ? 'on' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlag(m.id, !m.is_flagged);
                  }}
                  title={m.is_flagged ? 'Снять отметку' : 'Отметить'}
                >
                  {m.is_flagged ? '⭐' : '☆'}
                </span>
                <div className="mail-row-main">
                  <div className="mail-row-from">{m.from_addr || '—'}</div>
                  <div className="mail-row-subject">
                    {m.subject || <em>(без темы)</em>}
                    {m.has_attachments && <span title="С вложением"> 📎</span>}
                  </div>
                  <div className="mail-row-snippet">{m.snippet}</div>
                </div>
                <div className="mail-row-date">{formatDate(m.date)}</div>
              </div>
            ))}
            <div style={{ padding: 12, textAlign: 'center' }}>
              <button
                className="btn-secondary"
                onClick={handleLoadOlder}
                disabled={status === 'syncing'}
              >
                📥 Загрузить старее
              </button>
            </div>
            </>
          )}
        </div>
      </section>

      {/* Reader */}
      <section className="mail-reader">
        {!selectedMessage ? (
          <div className="mail-hint" style={{ padding: 40 }}>
            ← Выберите письмо
          </div>
        ) : (
          <div className="mail-reader-inner">
            <button
              className="mobile-only mail-back-btn"
              onClick={() => setMobileView('list')}
            >
              <i className="bi bi-arrow-left"></i> К списку
            </button>
            <div className="mail-reader-header">
              <h2>{selectedMessage.subject || '(без темы)'}</h2>
              <div className="mail-reader-meta">
                <div>
                  <strong>От:</strong> {selectedMessage.from_addr}
                </div>
                <div>
                  <strong>Кому:</strong> {selectedMessage.to_addrs}
                </div>
                {selectedMessage.cc_addrs && (
                  <div>
                    <strong>Копия:</strong> {selectedMessage.cc_addrs}
                  </div>
                )}
                <div className="mail-reader-date">{formatDate(selectedMessage.date)}</div>
              </div>
              <div className="mail-reader-actions">
                <button
                  className="btn-secondary"
                  onClick={() => handleFlag(selectedMessage.id, !selectedMessage.is_flagged)}
                >
                  {selectedMessage.is_flagged ? '⭐ Снять' : '☆ Отметить'}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() =>
                    setComposerOpen({
                      mode: 'reply',
                      replyTo: {
                        messageId: selectedMessage.id,
                        subject: selectedMessage.subject,
                        from_addr: selectedMessage.from_addr,
                        body_text: selectedMessage.body_text,
                        body_html: selectedMessage.body_html,
                      },
                    })
                  }
                >
                  ↩️ Ответить
                </button>
                <button
                  className="btn-secondary"
                  onClick={() =>
                    setComposerOpen({
                      mode: 'forward',
                      forwardFrom: {
                        subject: selectedMessage.subject,
                        body_text: selectedMessage.body_text,
                        body_html: selectedMessage.body_html,
                      },
                    })
                  }
                >
                  ➡️ Переслать
                </button>
                <button className="btn-danger" onClick={() => handleDelete(selectedMessage.id)}>
                  🗑️ Удалить
                </button>
              </div>
            </div>

            {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
              <div className="mail-attachments">
                <strong>Вложения:</strong>
                {selectedMessage.attachments.map((a) => (
                  <a
                    key={a.id}
                    className="mail-attachment"
                    href={emailApi.attachmentUrl(selectedMessage.id, a.id)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    📎 {a.filename || 'file'}{' '}
                    <small>{a.size_bytes ? `(${Math.ceil(a.size_bytes / 1024)} KB)` : ''}</small>
                  </a>
                ))}
              </div>
            )}

            <div className="mail-reader-body">
              {selectedMessage.body_html ? (
                <div
                  className="mail-body-html"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(selectedMessage.body_html),
                  }}
                />
              ) : (
                <pre className="mail-body-text">{selectedMessage.body_text}</pre>
              )}
            </div>
          </div>
        )}
      </section>

      {composerOpen && activeAccountId && (
        <Composer
          accounts={accounts}
          defaultAccountId={activeAccountId}
          mode={composerOpen.mode}
          replyTo={composerOpen.replyTo}
          forwardFrom={composerOpen.forwardFrom}
          onClose={() => setComposerOpen(null)}
          onSent={() => {
            setComposerOpen(null);
            loadMessages();
          }}
        />
      )}
    </div>
  );
};

// ── helpers ───────────────────────────────────────────────────

function folderIcon(special?: string): string {
  switch (special) {
    case 'inbox':
      return '📥';
    case 'sent':
      return '📤';
    case 'drafts':
      return '📝';
    case 'trash':
      return '🗑️';
    case 'junk':
      return '🚫';
    default:
      return '📁';
  }
}

function folderIconBi(special?: string): string {
  switch (special) {
    case 'inbox':
      return 'inbox-fill';
    case 'sent':
      return 'send-fill';
    case 'drafts':
      return 'file-earmark-text';
    case 'trash':
      return 'trash3';
    case 'junk':
      return 'shield-exclamation';
    case 'archive':
      return 'archive';
    default:
      return 'folder';
  }
}

const FOLDER_LABELS_BY_SPECIAL: Record<string, string> = {
  inbox: 'Входящие',
  sent: 'Отправленные',
  drafts: 'Черновики',
  trash: 'Корзина',
  junk: 'Спам',
  archive: 'Архив',
  all: 'Вся почта',
  flagged: 'Помеченные',
  important: 'Важное',
};

// Английские имена системных папок IMAP (когда сервер не возвращает SPECIAL-USE flag)
const FOLDER_LABELS_BY_NAME: Record<string, string> = {
  inbox: 'Входящие',
  sent: 'Отправленные',
  'sent items': 'Отправленные',
  drafts: 'Черновики',
  trash: 'Корзина',
  deleted: 'Корзина',
  'deleted items': 'Корзина',
  spam: 'Спам',
  junk: 'Спам',
  archive: 'Архив',
  outbox: 'Исходящие',
  templates: 'Шаблоны',
  template: 'Шаблоны',
  gmail: 'Gmail',
};

function folderLabel(folder: EmailFolder): string {
  if (folder.special && FOLDER_LABELS_BY_SPECIAL[folder.special]) {
    return FOLDER_LABELS_BY_SPECIAL[folder.special];
  }
  const lower = (folder.name || '').toLowerCase().trim();
  if (FOLDER_LABELS_BY_NAME[lower]) return FOLDER_LABELS_BY_NAME[lower];
  // Вложенные папки типа "Drafts|template" → "Черновики / шаблоны"
  if (lower.includes('|') || lower.includes('/')) {
    const parts = (folder.name || '').split(/[|/]/).map((p) => {
      const k = p.toLowerCase().trim();
      return FOLDER_LABELS_BY_NAME[k] || p;
    });
    return parts.join(' / ');
  }
  return folder.name || folder.raw;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const sameYear = d.getFullYear() === now.getFullYear();
  if (sameYear) return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Полноценная DOMPurify-санитизация HTML-тела письма.
// Запрещаем JS, формы, iframe-ы кроме безопасных embed'ов.
const PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'a', 'b', 'i', 'em', 'strong', 'u', 's', 'strike', 'sub', 'sup', 'small',
    'p', 'br', 'hr', 'span', 'div', 'pre', 'code', 'blockquote', 'q',
    'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup', 'col',
    'img', 'figure', 'figcaption',
    'font', 'center', 'big',
  ],
  ALLOWED_ATTR: [
    'href', 'title', 'target', 'rel',
    'src', 'alt', 'width', 'height',
    'style', 'class', 'id',
    'colspan', 'rowspan', 'align', 'valign', 'cellpadding', 'cellspacing', 'border', 'bgcolor',
    'color', 'face', 'size',
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'meta', 'link'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'formaction'],
};

function sanitizeHtml(html: string): string {
  if (!html) return '';
  const clean = DOMPurify.sanitize(html, PURIFY_CONFIG);
  // Открывать ссылки в новой вкладке + rel="noopener noreferrer"
  return clean.replace(
    /<a\s+([^>]*?)href=/gi,
    '<a target="_blank" rel="noopener noreferrer" $1href=',
  );
}

export default MailPage;
